/**
 *                               *** HIGHLY CONFIDENTIAL ***
 *
 * All code written here is confidential and proprietary of Celligence International, LLC.
 * The algorithms on this file are considered undisclosed material information and should
 * not be disclosed to any third party without proper legal advise. Any misuse, including
 * but not limited to the disclosures of such algorithms contained herein to third parties
 * are subject to legal actions. Only executive members are allowed to review and update
 * parts of this code.
 *
 *                    *** Copyright© Celligence International, LLC - 2024 ***
 *
 */
import axios from 'axios';
import { getServicerUser, setLoggedOut, removeUserSession } from '../storage/userStorage';

/**
 * Axios wrapper to call the API's
 * @param request: holds the service, url, method, and data to call the API
 * @param config (optional): config that you want in the request
 * @param headers (optional): headers to be included in the request
 * @returns response or error
 */

type AxiosCallOptions = {
  redirectOnUnauthorized?: boolean;
};

// NOTICE: headers should include { "Session-Token": token } where token is from useAuthContext
const axiosCall = async (
  request: { url: string; method?: string; data?: any; responseType?: 'json' | 'blob' },
  config = {},
  headers = {},
  useRestBase = true,
  options: AxiosCallOptions = {},
) => {
  const { redirectOnUnauthorized = true } = options;
  const { url: URL, method, data, responseType } = request;

  if (URL === undefined || URL === null || URL === '') {
    throw new Error('Unable to fulfill the request, endpoint not defined.');
  }

  try {
    const url = useRestBase ? process.env[`REACT_APP_REST_BASE`] + (`/${URL}` || '') : URL;
    const response = await axios({
      ...config,
      method: method || 'get',
      headers: { ...headers },
      url,
      data,
      responseType: responseType || 'json',
    });
    return response && response.data ? response.data : null;
  } catch (err: any) {
    if (
      redirectOnUnauthorized &&
      err?.response?.status === 401 &&
      err?.response?.headers?.['referrer-policy'] !== 'strict-origin-when-cross-origin'
    ) {
      let loginPath = '/login';
      if (getServicerUser()) loginPath = '/login-servicer';
      removeUserSession();
      setLoggedOut(true);
      window.location.replace(process.env[`REACT_APP_HOMEPAGE_PATH`] + loginPath);
    }
    throw err;
  }
};

export default axiosCall;
